/**
 * Query Database Tool
 * ElevenLabs webhook tool for querying databases during conversations
 */

import type { QueryToolRequest, QueryToolResponse, ExecutionContext } from '../types';

export interface QueryDatabaseToolConfig {
  type: 'webhook';
  name: string;
  description: string;
  api_schema: {
    url: string;
    method: 'POST';
    headers: Record<string, string>;
    request_body_schema: {
      type: 'object';
      properties: Record<string, any>;
      required: string[];
    };
  };
}

/**
 * Get the query_database webhook tool configuration for ElevenLabs
 */
export function getQueryDatabaseTool(domain: string, secret: string, agentId: string): QueryDatabaseToolConfig {
  const webhookUrl = `${domain}/api/plugins/database-connector/tools/query/${secret}/${agentId}`;

  return {
    type: 'webhook',
    name: 'query_database',
    description: `Search and retrieve information from connected databases. Use this tool when the customer asks about:
- Account balance or credit
- Order status or history
- Payment status or due amounts
- Appointment details
- Any stored customer data

Examples:
- "ما رصيدي؟" → Query balance
- "ما حالة طلبي؟" → Query order status
- "كم المبلغ المستحق علي؟" → Query pending payments
- "متى موعدي القادم؟" → Query appointments`,
    api_schema: {
      url: webhookUrl,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      request_body_schema: {
        type: 'object',
        properties: {
          query: {
            type: 'string',
            description: 'The natural language question or data request from the customer',
          },
          context: {
            type: 'object',
            description: 'Additional context like specific IDs mentioned by the customer',
            properties: {
              order_id: { type: 'string' },
              invoice_id: { type: 'string' },
              date_from: { type: 'string' },
              date_to: { type: 'string' },
            },
          },
        },
        required: ['query'],
      },
    },
  };
}

/**
 * Handle the query_database tool call
 */
export async function handleQueryDatabaseTool(
  request: QueryToolRequest,
  context: ExecutionContext,
  services: {
    connectionManager: any;
    queryEngine: any;
    textToSQL?: any;
  }
): Promise<QueryToolResponse> {
  console.log(`[Query Tool] Processing: "${request.query.substring(0, 50)}..."`);

  try {
    // Get agent's connected databases
    const agentConnections = await getAgentConnections(context.agentId!, services);

    if (!agentConnections.length) {
      return {
        success: false,
        response: 'لا توجد قواعد بيانات متصلة بهذا الوكيل.',
      };
    }

    // Try to match with saved queries first
    const matchedQuery = await findMatchingQuery(request.query, agentConnections, services);

    if (matchedQuery) {
      // Execute the matched saved query
      const result = await services.queryEngine.executeQuery(
        matchedQuery.id,
        { ...request.context, ...context },
        context
      );

      return {
        success: true,
        response: result.formattedResponse || formatQueryResult(result.data),
        data: result.data,
        sources: [{ queryId: matchedQuery.id, tableName: matchedQuery.name }],
      };
    }

    // If text-to-SQL is enabled, try to generate a query
    if (services.textToSQL) {
      const sqlResult = await services.textToSQL.convertToSQL(
        request.query,
        agentConnections[0].connectionId,
        context
      );

      if (sqlResult.success) {
        const result = await services.connectionManager.executeQuery(
          agentConnections[0].connectionId,
          sqlResult.sql,
          sqlResult.params
        );

        return {
          success: true,
          response: formatQueryResult(result.rows),
          data: result.rows,
        };
      }
    }

    return {
      success: false,
      response: 'لم أتمكن من العثور على المعلومات المطلوبة. هل يمكنك توضيح سؤالك؟',
    };
  } catch (error: any) {
    console.error('[Query Tool] Error:', error);
    return {
      success: false,
      response: 'حدث خطأ أثناء البحث عن المعلومات. يرجى المحاولة مرة أخرى.',
    };
  }
}

/**
 * Format query results for voice response
 */
function formatQueryResult(data: any[]): string {
  if (!data || data.length === 0) {
    return 'لم يتم العثور على أي نتائج.';
  }

  if (data.length === 1) {
    const item = data[0];
    const parts: string[] = [];

    for (const [key, value] of Object.entries(item)) {
      if (value !== null && value !== undefined) {
        parts.push(`${translateKey(key)}: ${formatValue(value)}`);
      }
    }

    return parts.join('. ');
  }

  return `تم العثور على ${data.length} نتائج. ${formatFirstItems(data, 3)}`;
}

function translateKey(key: string): string {
  const translations: Record<string, string> = {
    balance: 'الرصيد',
    amount: 'المبلغ',
    status: 'الحالة',
    order_id: 'رقم الطلب',
    date: 'التاريخ',
    due_date: 'تاريخ الاستحقاق',
    customer_name: 'اسم العميل',
    phone: 'الهاتف',
    total: 'الإجمالي',
    pending: 'معلق',
    paid: 'مدفوع',
    name: 'الاسم',
  };
  return translations[key.toLowerCase()] || key;
}

function formatValue(value: any): string {
  if (typeof value === 'number') {
    return value.toLocaleString('ar-SA');
  }
  if (value instanceof Date) {
    return value.toLocaleDateString('ar-SA');
  }
  if (typeof value === 'boolean') {
    return value ? 'نعم' : 'لا';
  }
  return String(value);
}

function formatFirstItems(data: any[], count: number): string {
  return data.slice(0, count).map((item, i) => {
    const firstValue = Object.values(item)[0];
    return `${i + 1}. ${formatValue(firstValue)}`;
  }).join('. ');
}

async function getAgentConnections(agentId: string, services: any): Promise<any[]> {
  // Get agent connections from database
  return [];
}

async function findMatchingQuery(query: string, connections: any[], services: any): Promise<any | null> {
  // Match query against saved queries' natural language triggers
  return null;
}
